import React, { useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import SessionStore from '../State/SessionStore'
import LoadingSpinner from '../Components/LoadingSpinner'
import AuthPage from './AuthPage'
import TimesheetAppPage from './TimesheetApp/TimesheetAppPage'
import { authClient } from '../lib/auth-client'
import { Route, Switch, useLocation } from 'wouter'
import { trpc } from '../system/trpc'
import { handleMonumentTransitionMode } from '../lib/monument-transition'

const CHECK_INTERVAL_MS = 15_000

export default observer(() => {
	const { data: session, isPending } = authClient.useSession()
	const [location] = useLocation()
	const [transitionLocked, setTransitionLocked] = useState(false)
	const [accessPending, setAccessPending] = useState(true)

	useEffect(() => {
		if (!session?.user?.id) {
			setAccessPending(false)
			return
		}

		setAccessPending(true)
		let active = true
		const checkAccess = async () => {
			try {
				const access = await trpc.auth.getCoincraftAccess.query()
				if (!active) return
				setTransitionLocked(
					!access.allowed &&
						handleMonumentTransitionMode(access.mode)
				)
			} catch (error) {
				console.error('Unable to check Coincraft transition access', error)
			} finally {
				if (active) setAccessPending(false)
			}
		}

		checkAccess()
		const interval = window.setInterval(checkAccess, CHECK_INTERVAL_MS)
		window.addEventListener('focus', checkAccess)

		return () => {
			active = false
			window.clearInterval(interval)
			window.removeEventListener('focus', checkAccess)
		}
	}, [session?.user?.id])

	useEffect(() => {
		// Sync better-auth session changes with SessionStore
		const syncSession = async () => {
			if (!isPending && SessionStore.initialized) {
				if (session?.user) {
					// Session exists, update SessionStore if needed
					if (SessionStore.userId !== session.user.id) {
						await SessionStore.checkAuthentication()
					}
				} else {
					// No session, logout if needed
					if (SessionStore.authenticated) {
						await SessionStore.clearCache()
					}
				}
			}
		}

		syncSession()
	}, [session, isPending])

	// Handle loading state
	if (!SessionStore.initialized || isPending || accessPending) {
		return <LoadingSpinner />
	}

	if (transitionLocked) {
		return (
			<main className="flex min-h-screen items-center justify-center bg-white p-6">
				<div className="w-full max-w-lg rounded-lg border border-gray-200 p-6 shadow-sm">
					<h1 className="mb-3 text-xl font-semibold">
						We’re preparing your move to Monument
					</h1>
					<p className="text-sm text-gray-600">
						Your organisation’s administrators are verifying your data. Coincraft is temporarily unavailable for your account.
					</p>
				</div>
			</main>
		)
	}

	// If not authenticated and not on an auth route, redirect to login
	if (!SessionStore.authenticated && !location.startsWith('/auth')) {
		return <AuthPage authView="sign-in" />
	}

	// If authenticated but on an auth route, allow it (needed for account-details, etc.)
	if (SessionStore.authenticated && location.startsWith('/auth')) {
		// Extract the auth view from the location
		const authView = location.replace('/auth/', '')
		return <AuthPage authView={authView || 'sign-in'} />
	}

	// Authenticated users get the router
	return (
		<Switch>
			{/* Auth routes */}
			<Route path="/auth/:authView">
				{(params) => <AuthPage authView={params.authView} />}
			</Route>

			{/* Default route - Timesheet app */}
			<Route path="/">
				<TimesheetAppPage />
			</Route>

			{/* Catch-all route */}
			<Route>
				<TimesheetAppPage />
			</Route>
		</Switch>
	)
})
